import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, CheckCircle2, AlertTriangle, History, Trash2, ChevronLeft } from "lucide-react";
import { useAuth } from "@/App";
import KundeStammdatenForm, { LEERE_STAMMDATEN, useDirty, useUnsavedWarning } from "@/components/einsatz/KundeStammdatenForm";
import VerfuegbarkeitsBogenForm, { LEERER_BOGEN, bogenZumSenden } from "@/components/einsatz/VerfuegbarkeitsBogenForm";
import { formatApiError, STATUS_LABEL, fmtTs, leereTage } from "@/components/einsatz/felder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = (user) => ({ headers: { Authorization: `Bearer ${user.token}` } });

export default function KundeEditor({ kundeId, onBack, onDeleted, staffExtras }) {
  const { user } = useAuth();
  const [kunde, setKunde] = useState(null);
  const [stamm, setStamm] = useState(LEERE_STAMMDATEN);
  const [bogen, setBogen] = useState(LEERER_BOGEN);
  const [fehler, setFehler] = useState({});
  const [hinweise, setHinweise] = useState([]);
  const [saving, setSaving] = useState(null);
  const [schritt, setSchritt] = useState(1);
  const [dirty, markSaved] = useDirty({ stamm, bogen });
  const versionRef = useRef(null);
  useUnsavedWarning(dirty);
  useEffect(() => { if (kunde) { versionRef.current = kunde.version; markSaved(); } }, [kunde]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/einsatz/kunden/${kundeId}`, H(user));
    setKunde(data);
    setStamm({ ...LEERE_STAMMDATEN, ...Object.fromEntries(Object.keys(LEERE_STAMMDATEN).map((k) => [k, data[k] || ""])) });
    setBogen(data.bogen ? { ...LEERER_BOGEN, ...data.bogen, einsatzdauer_min: data.bogen.einsatzdauer_min ?? "", einsaetze_pro_rhythmus: data.bogen.einsaetze_pro_rhythmus ?? "", tage: { ...leereTage(), ...(data.bogen.tage || {}) } } : LEERER_BOGEN);
    return data;
  }, [kundeId, user]);

  useEffect(() => { load().catch(() => toast.error("Kunde konnte nicht geladen werden")); }, [load]);

  const handleConflict = (err) => {
    const d = err.response?.data?.detail;
    toast.error(formatApiError(d), { description: d?.updated_by ? `Zuletzt geändert von ${d.updated_by.name} am ${fmtTs(d.updated_at)}. Bitte Seite neu laden.` : undefined, duration: 8000 });
  };

  const speichern = async (abschliessen) => {
    setSaving(abschliessen ? "final" : "entwurf");
    setFehler({});
    try {
      const k = await axios.patch(`${API}/einsatz/kunden/${kundeId}`, { ...stamm, version: versionRef.current }, H(user));
      const res = await axios.put(`${API}/einsatz/kunden/${kundeId}/bogen`, { bogen: bogenZumSenden(bogen), abschliessen, version: k.data.version }, H(user));
      setKunde(res.data);
      setHinweise(res.data.hinweise || []);
      toast.success(abschliessen ? "Erfassung abgeschlossen" : "Entwurf gespeichert");
    } catch (err) {
      const st = err.response?.status;
      const d = err.response?.data?.detail;
      if (st === 409) handleConflict(err);
      else if (st === 422 && d?.fehler) { setFehler(d.fehler); setHinweise(d.hinweise || []); toast.error("Bitte markierte Felder prüfen"); if (d.fehler.stammdaten) setSchritt(1); else setSchritt(2); }
      else toast.error(formatApiError(d, "Speichern fehlgeschlagen – Eingaben bleiben erhalten"));
      // Nur Versionsstand aktualisieren – Eingaben im Formular bleiben erhalten
      axios.get(`${API}/einsatz/kunden/${kundeId}`, H(user)).then((r) => { versionRef.current = r.data.version; }).catch(() => {});
    }
    setSaving(null);
  };

  const loeschen = async () => {
    if (!window.confirm(`Kunde ${kunde.vorname} ${kunde.nachname} wirklich löschen?`)) return;
    try { await axios.delete(`${API}/einsatz/kunden/${kundeId}`, H(user)); toast.success("Kunde gelöscht"); onDeleted?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!kunde) return <div className="flex justify-center p-10"><Loader2 className="h-8 w-8 animate-spin text-cyan-700" /></div>;
  const st = STATUS_LABEL[kunde.status] || STATUS_LABEL.nicht_erfasst;
  const kannLoeschen = user.role === "admin";

  return (
    <div className="space-y-5 pb-28" data-testid="kunde-editor">
      <div className="flex items-start gap-3">
        {onBack && <button onClick={() => { if (!dirty || window.confirm("Ungespeicherte Änderungen verwerfen?")) onBack(); }} className="h-11 w-11 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0" aria-label="Zurück" data-testid="kunde-back"><ChevronLeft className="h-5 w-5" /></button>}
        <div className="flex-1 min-w-0">
          <h1 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 truncate" data-testid="kunde-titel">{kunde.vorname} {kunde.nachname}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
            <span className={`px-2 py-0.5 rounded-full font-semibold ${st.cls}`} data-testid="kunde-status">{st.l}</span>
            {kunde.freigegeben_fuer_mich && <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200 font-semibold" data-testid="kunde-freigegeben-badge">Für mich freigegeben</span>}
            <span>Angelegt von {kunde.angelegt_von?.name}</span><span>· Zuletzt {fmtTs(kunde.updated_at)} von {kunde.updated_by?.name}</span>
          </div>
        </div>
      </div>
      {dirty && <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-2" data-testid="unsaved-hint"><AlertTriangle className="h-4 w-4" />Ungespeicherte Änderungen</div>}
      {staffExtras && staffExtras(kunde, load)}

      <div className="grid grid-cols-2 gap-2" role="tablist">
        {[["1", "Kundendaten"], ["2", "Verfügbarkeitsbogen"]].map(([n, l]) => <button key={n} role="tab" onClick={() => setSchritt(Number(n))} className={`h-12 rounded-xl text-sm font-semibold ${schritt === Number(n) ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-700"}`} data-testid={`schritt-${n}`}>{n}. {l}</button>)}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6">
        {schritt === 1 ? <KundeStammdatenForm wert={stamm} onChange={setStamm} fehler={fehler.stammdaten ? Object.fromEntries(["vorname", "nachname", "strasse", "hausnummer", "plz", "ort"].filter((f) => !stamm[f]?.trim()).map((f) => [f, "Erforderlich für den Abschluss"])) : {}} /> : <VerfuegbarkeitsBogenForm wert={bogen} onChange={setBogen} fehler={fehler} hinweise={hinweise} />}
        {fehler.stammdaten && schritt === 1 && <p className="text-sm font-semibold text-red-600 mt-3" role="alert">{fehler.stammdaten}</p>}
        {schritt === 1 && <button onClick={() => setSchritt(2)} className="mt-5 w-full h-12 rounded-xl bg-cyan-50 text-cyan-800 font-semibold border border-cyan-200" data-testid="weiter-zu-bogen">Weiter zum Verfügbarkeitsbogen →</button>}
      </div>

      {kannLoeschen && <button onClick={loeschen} className="text-sm text-red-600 inline-flex items-center gap-1.5 h-10" data-testid="kunde-loeschen"><Trash2 className="h-4 w-4" />Kunde löschen</button>}
      {kunde.bogen_historie?.length > 0 && <p className="text-xs text-slate-500 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" />{kunde.bogen_historie.length} frühere Bogen-Version(en) archiviert</p>}

      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 p-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
        <div className="max-w-3xl mx-auto grid grid-cols-2 gap-3">
          <button onClick={() => speichern(false)} disabled={!!saving} className="h-14 rounded-xl bg-white border-2 border-slate-300 text-slate-800 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="entwurf-speichern">{saving === "entwurf" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}Entwurf speichern</button>
          <button onClick={() => speichern(true)} disabled={!!saving} className="h-14 rounded-xl bg-cyan-700 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="erfassung-abschliessen">{saving === "final" ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}Erfassung abschließen</button>
        </div>
      </div>
    </div>
  );
}

export function KundeAnlegenForm({ onCreated, onCancel }) {
  const { user } = useAuth();
  const [stamm, setStamm] = useState(LEERE_STAMMDATEN);
  const [fehler, setFehler] = useState({});
  const [dublette, setDublette] = useState(null);
  const [saving, setSaving] = useState(false);

  const anlegen = async (ignorieren = false) => {
    const f = {};
    if (!stamm.vorname.trim()) f.vorname = "Vorname erforderlich";
    if (!stamm.nachname.trim()) f.nachname = "Nachname erforderlich";
    setFehler(f);
    if (Object.keys(f).length) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/einsatz/kunden`, { ...stamm, dublette_ignorieren: ignorieren }, H(user));
      toast.success("Kunde angelegt – jetzt Verfügbarkeitsbogen ausfüllen");
      onCreated(data);
    } catch (err) {
      if (err.response?.status === 409) setDublette(err.response.data.detail);
      else toast.error(formatApiError(err.response?.data?.detail, "Anlegen fehlgeschlagen"));
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="kunde-anlegen">
      <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-6"><KundeStammdatenForm wert={stamm} onChange={setStamm} fehler={fehler} /></div>
      {dublette && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="dubletten-hinweis">
          <p className="font-semibold text-amber-900 inline-flex items-center gap-2"><AlertTriangle className="h-5 w-5" />Möglicherweise existiert dieser Kunde bereits</p>
          {dublette.zugaenglich?.map((k) => <button key={k.id} onClick={() => onCreated({ id: k.id, vorhanden: true })} className="w-full text-left h-12 px-3 rounded-xl bg-white border border-amber-200 text-sm font-semibold text-slate-800" data-testid={`dublette-oeffnen-${k.id}`}>{k.vorname} {k.nachname}{k.ort ? `, ${k.ort}` : ""} – öffnen und ergänzen</button>)}
          {dublette.fremde_anzahl > 0 && <p className="text-sm text-amber-900">{dublette.fremde_anzahl} ähnliche(r) Eintrag/Einträge außerhalb Ihres Zugriffs. Bei Neuanlage wird der Fall der Verwaltung zur Prüfung markiert.</p>}
          <button onClick={() => anlegen(true)} disabled={saving} className="w-full h-12 rounded-xl bg-amber-600 text-white font-semibold" data-testid="dublette-trotzdem">Trotzdem neu anlegen</button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {onCancel && <button onClick={onCancel} className="h-14 rounded-xl bg-white border-2 border-slate-300 font-bold text-slate-800" data-testid="kunde-anlegen-abbrechen">Abbrechen</button>}
        <button onClick={() => anlegen(false)} disabled={saving} className={`h-14 rounded-xl bg-cyan-700 text-white font-bold inline-flex items-center justify-center gap-2 ${onCancel ? "" : "col-span-2"}`} data-testid="kunde-anlegen-submit">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null}Kunde anlegen & Bogen ausfüllen</button>
      </div>
    </div>
  );
}
